// DashboardTintedCard (Aug 2026 colorful redesign).
//
// Shared full-color KPI / status card. Replaces the sea of white
// boxes in the Finance + Operations dashboards with a tinted surface
// per tone (blue / green / teal / purple / amber / red / navy / mint).
// A subtle CSS radial bloom in the top-right adds premium depth
// without needing any image assets — pure CSS.
//
// Consumers pass:
//   tone       — palette entry (dashboardTheme::toneMap)
//   icon       — MUI icon element (fontSize inherited)
//   label      — small uppercase eyebrow (KPI label)
//   value      — big number/text (formatted currency, count, %)
//   hint       — supporting caption underneath
//   interactive — enables hover lift (for clickable KPIs / cards)
//   accentBar  — draws a left-edge accent stripe (used on alert rows)

import React from "react";
import { Box, Typography } from "@mui/material";
import { dashTokens, toneMap, Tone } from "./dashboardTheme";

interface Props {
  tone: Tone;
  icon?: React.ReactNode;
  label?: React.ReactNode;
  value?: React.ReactNode;
  hint?: React.ReactNode;
  interactive?: boolean;
  accentBar?: boolean;
  padding?: number | string;
  minHeight?: number | string;
  onClick?: () => void;
  children?: React.ReactNode;
  sx?: any;
  /** Add a decorative radial bloom in the top-right corner. */
  bloom?: boolean;
}

export const DashboardTintedCard: React.FC<Props> = ({
  tone, icon, label, value, hint, interactive, accentBar,
  padding = 2, minHeight, onClick, children, sx, bloom = true,
}) => {
  const t = toneMap[tone];

  return (
    <Box
      onClick={onClick}
      sx={{
        position: "relative", overflow: "hidden",
        background: t.grad,
        border: `1px solid ${t.border}`,
        borderRadius: `${dashTokens.radius.md}px`,
        boxShadow: dashTokens.shadow.card,
        p: padding, minHeight,
        cursor: interactive || onClick ? "pointer" : "default",
        transition: dashTokens.transition,
        ...(interactive || onClick ? {
          "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: dashTokens.shadow.cardHover,
            borderColor: t.iconBg,
            "& .dashcard-icon": { transform: "scale(1.04)" },
          },
        } : {}),
        ...(accentBar ? {
          "&::after": {
            content: '""', position: "absolute",
            left: 0, top: 0, bottom: 0, width: 4,
            background: t.iconBg,
          },
        } : {}),
        ...sx,
      }}
    >
      {/* Decorative bloom removed — the 2026-09 Full-Fill Rainbow
          v2 pass forbids ANY floating decorative shapes inside cards
          (spec §"COMPONENT RESTRICTIONS"). The `bloom` prop is kept
          on the component API for backwards-compat with existing
          callers but is now a no-op. */}

      <Box sx={{ position: "relative" }}>
        {(icon || label) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: value ? 1 : 0.5 }}>
            {icon && (
              <Box className="dashcard-icon" sx={{
                width: 34, height: 34, borderRadius: `${dashTokens.radius.sm}px`,
                bgcolor: t.iconBg, color: t.iconFg,
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: `0 4px 12px ${t.iconBg}30`,
                transition: dashTokens.transition,
                "& svg": { fontSize: 18 },
              }}>
                {icon}
              </Box>
            )}
            {label && (
              <Typography sx={{
                fontSize: 11, fontWeight: 700, color: t.accent,
                textTransform: "uppercase", letterSpacing: "0.08em",
                opacity: 0.85,
              }}>
                {label}
              </Typography>
            )}
          </Box>
        )}

        {value !== undefined && value !== null && (
          <Typography sx={{
            fontSize: 28, fontWeight: 800, color: t.accent,
            lineHeight: 1.1, letterSpacing: "-0.02em",
          }}>
            {value}
          </Typography>
        )}

        {hint && (
          <Typography sx={{
            fontSize: 11.5, color: t.accent, opacity: 0.75, mt: 0.5, fontWeight: 500,
          }}>
            {hint}
          </Typography>
        )}

        {children}
      </Box>
    </Box>
  );
};

/**
 * Small skeleton placeholder inside a DashboardTintedCard while data
 * loads — keeps the tinted surface visible instead of flashing "$0"
 * or "—" (spec §35 loading requirement).
 */
export const DashboardTintedSkeleton: React.FC<{ tone: Tone }> = ({ tone }) => {
  const t = toneMap[tone];
  // Solid-colour pulse (no linear-gradient sweep — per 2026-09
  // Rainbow Colour Theory pass, gradients are banned everywhere).
  return (
    <Box sx={{
      height: 26, width: "60%", borderRadius: "6px",
      bgcolor: t.iconBg,
      animation: "dashSkeleton 1.2s ease-in-out infinite",
      "@keyframes dashSkeleton": {
        "0%":   { opacity: 0.18 },
        "50%":  { opacity: 0.32 },
        "100%": { opacity: 0.18 },
      },
    }} />
  );
};
